/**
 * Practice Plan Trial Modal
 *
 * Team signups (org size > "Just me") with Practice plan trial — practice-variant
 * sibling of ClinicianTrialModal. Queue selection lives in launchModalsConfigAtom;
 * BE grants CLINICIAN_PRO_PLUS + team on first non-solo org-size write, so this
 * modal only dismisses (no activate call).
 *
 * Primary CTA may open PracticeInviteTeamModal for legacy `/` self-onboarding
 * (no InviteTeamStep). V2 marks invite-step done in sessionStorage and skips
 * that chain — CTAs collapse to single "Got it" with practice icon.
 *
 * Figma: https://www.figma.com/design/edgIeajMOqpAxGbdqcq2oZ/Sign-up---onboarding?node-id=9645-4022&m=dev
 */

import { TrialStartModalBase } from '@/components/modal/trial-start-modal-base';
import { IS_PRACTICE_INVITE_FLOW_ENABLED } from '@/features/flags';
import {
  inviteTeamStepCompletedAtom,
  practiceTrialModalPendingAtom,
} from '@/features/growth/practice-invite-team-members/atoms';
import { useFeatureLists } from '@/hooks/useFeatureLists';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import {
  trackPracticeTrialClick,
  trackPracticeTrialViewed,
} from '@/models/analytics/features/trackPracticeTrialInviteAnalytics';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import {
  practiceInviteTeamModalOpenAtom,
  practiceTrialModalMessages,
} from '@heidi/commerce';
import {
  PRACTICE_INVITE_ACTION,
  PRACTICE_TRIAL_ACTION,
  TRIALS_AND_UPGRADES_SCREEN,
} from '@heidi/commerce/contracts';
import { Button } from '@heidi/design-system/button';
import { ModalId } from '@heidi/schema/ml-scribe';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

export const PracticeTrialModal = () => {
  const intl = useIntl();
  const { isOpen, handleClose, dismissible } = useQueuedModal(ModalId.PRACTICE_TRIAL);
  const setPracticeInviteTeamModalOpen = useSetAtom(practiceInviteTeamModalOpenAtom);
  const inviteTeamStepCompleted = useAtomValue(inviteTeamStepCompletedAtom);
  const setPracticeTrialModalPending = useSetAtom(practiceTrialModalPendingAtom);

  const { practiceTrialStartFeatures } = useFeatureLists();

  // Same gate as closeModal → invite chain. Invite done in onboarding ⇒ single
  // dismiss CTA; legacy `/` still gets invite + skip pair.
  const willOpenInviteModal =
    !!IS_PRACTICE_INVITE_FLOW_ENABLED() && !inviteTeamStepCompleted;

  useEffect(() => {
    if (isOpen) {
      trackPracticeTrialViewed({
        screen_name: TRIALS_AND_UPGRADES_SCREEN.practiceTrial,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Trial already activated by BE on org-size PATCH — close is a no-op for billing.
  const closeModal = useCallback(
    (openInviteFlow: boolean) => {
      setPracticeTrialModalPending(false);
      handleClose();
      if (openInviteFlow && willOpenInviteModal) {
        setPracticeInviteTeamModalOpen(true);
      }
    },
    [
      handleClose,
      setPracticeInviteTeamModalOpen,
      setPracticeTrialModalPending,
      willOpenInviteModal,
    ]
  );

  const primaryCtaMessage = willOpenInviteModal
    ? practiceTrialModalMessages.cta
    : practiceTrialModalMessages.gotIt;

  const handlePrimaryClick = useCallback(() => {
    trackPracticeTrialClick({
      screen_name: TRIALS_AND_UPGRADES_SCREEN.practiceTrial,
      action: PRACTICE_TRIAL_ACTION.getStarted,
      label: intl.formatMessage(primaryCtaMessage),
    });
    closeModal(willOpenInviteModal);
  }, [closeModal, intl, primaryCtaMessage, willOpenInviteModal]);

  const handleTryOnMyOwn = useCallback(() => {
    // Reports the INVITE modal's skip token, not one of this modal's own — the
    // two CTAs are the same funnel step. See
    // `practice-trial-invite-analytics.types.ts`.
    trackPracticeTrialClick({
      screen_name: TRIALS_AND_UPGRADES_SCREEN.practiceTrial,
      action: PRACTICE_INVITE_ACTION.skip,
      label: intl.formatMessage(practiceTrialModalMessages.tryOnMyOwn),
    });
    closeModal(false);
  }, [closeModal, intl]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setPracticeTrialModalPending(false);
        handleClose();
      }
    },
    [handleClose, setPracticeTrialModalPending]
  );

  if (isAppleCompliantBuild()) {
    return null;
  }

  return (
    <TrialStartModalBase
      open={isOpen}
      onOpenChange={handleOpenChange}
      dismissible={dismissible}
      hideCloseIcon
      variant="practice"
      headerIcon="gift"
      title={intl.formatMessage(practiceTrialModalMessages.title)}
      subtitle={intl.formatMessage(practiceTrialModalMessages.subtitle)}
      features={practiceTrialStartFeatures}
      primaryButtonText={intl.formatMessage(primaryCtaMessage)}
      footerText={intl.formatMessage(practiceTrialModalMessages.footerText)}
      hidePrimaryButtonIcon={willOpenInviteModal}
      onPrimaryClick={handlePrimaryClick}
      secondaryButton={
        willOpenInviteModal ? (
          <Button
            className="w-full"
            variant="outline"
            size="lg"
            onClick={handleTryOnMyOwn}
          >
            <FormattedMessage {...practiceTrialModalMessages.tryOnMyOwn} />
          </Button>
        ) : undefined
      }
    />
  );
};
